import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Button,
  Table,
  Spinner,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
} from 'reactstrap';
import { subscriptionService } from '../services';
import {
  SubscriptionInvoice,
  InvoicesListResponse,
  NotSubscribedUser,
  SubscriptionStatus,
} from '../types';
import BreadCrumb from '../components/Common/BreadCrumb';
import { toast } from 'react-toastify';
import { formatUkDate } from '../utils/dateFormat';

const STATUS_FILTER_OPTIONS: { value: '' | SubscriptionStatus; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'pending', label: 'Pending' },
  { value: 'trial', label: 'Trial' },
];

const InvoicesPage: React.FC = () => {
  const [invoicesData, setInvoicesData] = useState<InvoicesListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'' | SubscriptionStatus>('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [notSubscribedUsers, setNotSubscribedUsers] = useState<NotSubscribedUser[]>([]);
  const [loadingNotSubscribed, setLoadingNotSubscribed] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailTarget, setEmailTarget] = useState<'all' | 'selected'>('all');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [statusConfirmModal, setStatusConfirmModal] = useState<{
    subscriptionId: string;
    newStatus: 'active' | 'cancelled';
    userEmail?: string;
    userName?: string;
  } | null>(null);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const res = await subscriptionService.listInvoices({
        page,
        limit,
        ...(statusFilter ? { status: statusFilter } : {}),
      });
      setInvoicesData(res);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load invoices');
      setInvoicesData({ data: [], pagination: { total: 0, page: 1, limit: 10, totalPages: 0 } });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, [page, statusFilter]);

  const loadNotSubscribedUsers = async () => {
    setLoadingNotSubscribed(true);
    try {
      const users = await subscriptionService.getNotSubscribedUsers(500);
      setNotSubscribedUsers(users);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to load non-subscribed users');
      setNotSubscribedUsers([]);
    } finally {
      setLoadingNotSubscribed(false);
    }
  };

  const openStatusConfirmModal = (
    subscriptionId: string,
    newStatus: 'active' | 'cancelled',
    inv: SubscriptionInvoice,
  ) => {
    setStatusConfirmModal({
      subscriptionId,
      newStatus,
      userEmail: inv.userEmail,
      userName: inv.userName,
    });
  };

  const handleStatusConfirm = async () => {
    if (!statusConfirmModal) return;
    const { subscriptionId, newStatus } = statusConfirmModal;
    setStatusConfirmModal(null);
    setUpdatingId(subscriptionId);
    try {
      await subscriptionService.updateSubscriptionStatus(subscriptionId, newStatus);
      toast.success('Status updated');
      loadInvoices();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleVerifyLive = async (subscriptionId: string) => {
    setVerifyingId(subscriptionId);
    try {
      const result = await subscriptionService.verifyLiveSubscription(subscriptionId);
      if (result.changes?.statusChanged) {
        toast.success(`Updated: ${result.changes.oldStatus} → ${result.changes.newStatus}`);
      } else {
        toast.info('Subscription is up to date');
      }
      loadInvoices();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to verify with IAP');
    } finally {
      setVerifyingId(null);
    }
  };

  const openEmailModal = (target: 'all' | 'selected') => {
    setEmailTarget(target);
    setEmailForm({
      subject: 'Your subscription has ended',
      message:
        'Hi,\n\nYour access to our app has ended. Subscribe again to continue enjoying our content.\n\nBest regards',
    });
    if (target === 'all') {
      loadNotSubscribedUsers();
    }
    setEmailModalOpen(true);
  };

  const sendNotSubscribedEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailForm.subject.trim() || !emailForm.message.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    if (emailTarget === 'selected' && selectedUserIds.length === 0) {
      toast.error('Select at least one user, or use "Send email to all"');
      return;
    }
    setSendingEmail(true);
    try {
      const payload: any = {
        subject: emailForm.subject,
        message: emailForm.message,
      };
      if (emailTarget === 'selected' && selectedUserIds.length) {
        payload.recipientIds = selectedUserIds;
      }
      const res = await subscriptionService.sendNotSubscribedEmail(payload);
      toast.success(`Emails sent: ${res.count}`);
      setEmailModalOpen(false);
      setSelectedUserIds([]);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to send emails');
    } finally {
      setSendingEmail(false);
    }
  };

  const toggleUserSelection = (id: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const pagination = invoicesData?.pagination;
  const totalPages = pagination?.totalPages ?? 0;

  return (
    <>
      <BreadCrumb title="Invoices & Subscriptions" pageTitle="Invoices" />
      <Row>
        <Col xs={12}>
          <Card>
            <CardBody>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <h5 className="card-title mb-0">Subscriptions / Invoices</h5>
                <FormGroup className="mb-0 ms-auto">
                  <Input
                    type="select"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value as '' | SubscriptionStatus);
                      setPage(1);
                    }}
                    style={{ width: 'auto', display: 'inline-block' }}
                  >
                    {STATUS_FILTER_OPTIONS.map((opt) => (
                      <option key={opt.value || 'all'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </div>
              <div className="table-responsive">
                <Table className="table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>User</th>
                      <th>Plan</th>
                      <th>Status</th>
                      <th>Platform</th>
                      <th>Product ID</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Auto renew</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={10} className="text-center py-4">
                          <Spinner size="sm" /> Loading...
                        </td>
                      </tr>
                    ) : !invoicesData?.data?.length ? (
                      <tr>
                        <td colSpan={10} className="text-center py-4 text-muted">
                          No subscriptions found.
                        </td>
                      </tr>
                    ) : (
                      invoicesData.data.map((inv: SubscriptionInvoice) => (
                        <tr key={inv.id}>
                          <td>
                            <div>
                              <strong>{inv.userName || '—'}</strong>
                              <br />
                              <small className="text-muted">{inv.userEmail || '—'}</small>
                            </div>
                          </td>
                          <td>{inv.planType}</td>
                          <td>
                            <Badge
                              color={
                                inv.status === 'active'
                                  ? 'success'
                                  : inv.status === 'expired' || inv.status === 'cancelled'
                                    ? 'danger'
                                    : 'secondary'
                              }
                            >
                              {inv.status}
                            </Badge>
                          </td>
                          <td>
                            <Badge color="info">
                              {inv.platform || '—'}
                            </Badge>
                          </td>
                          <td>
                            <small className="text-muted">
                              {inv.productId ? inv.productId.split('.').pop() : '—'}
                            </small>
                          </td>
                          <td>
                            {inv.startDate
                              ? formatUkDate(inv.startDate)
                              : '—'}
                          </td>
                          <td>
                            {inv.endDate
                              ? formatUkDate(inv.endDate)
                              : '—'}
                          </td>
                          <td>{inv.autoRenew ? 'Yes' : 'No'}</td>
                          <td>
                            <div className="d-flex gap-1 flex-wrap">
                              <Button
                                size="sm"
                                color="primary"
                                outline
                                disabled={verifyingId === inv.id}
                                onClick={() => handleVerifyLive(inv.id)}
                                title="Query Apple/Google for live status"
                              >
                                {verifyingId === inv.id ? (
                                  <>
                                    <Spinner size="sm" /> Verifying...
                                  </>
                                ) : (
                                  'Verify Live'
                                )}
                              </Button>
                              {inv.status === 'active' ? (
                                <Button
                                  size="sm"
                                  color="danger"
                                  disabled={updatingId === inv.id}
                                  onClick={() =>
                                    openStatusConfirmModal(inv.id, 'cancelled', inv)
                                  }
                                >
                                  {updatingId === inv.id ? '...' : 'Set Cancelled'}
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  color="success"
                                  disabled={updatingId === inv.id}
                                  onClick={() =>
                                    openStatusConfirmModal(inv.id, 'active', inv)
                                  }
                                >
                                  {updatingId === inv.id ? '...' : 'Set Active'}
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              {(totalPages > 1 || pagination) && (
                <div className="pagination-bar">
                  {pagination && (
                    <div className="text-muted small mb-0">
                      Total: {pagination.total} · Page {pagination.page} of {pagination.totalPages}
                    </div>
                  )}
                  {totalPages > 1 && (
                    <Pagination className="pagination-rounded mb-0 ms-auto">
                      <PaginationItem disabled={page <= 1}>
                        <PaginationLink previous onClick={() => setPage((p) => Math.max(1, p - 1))} />
                      </PaginationItem>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                        .map((p, i, arr) => (
                          <React.Fragment key={p}>
                            {i > 0 && arr[i - 1] !== p - 1 && (
                              <PaginationItem disabled>
                                <PaginationLink>…</PaginationLink>
                              </PaginationItem>
                            )}
                            <PaginationItem active={p === page}>
                              <PaginationLink onClick={() => setPage(p)}>{p}</PaginationLink>
                            </PaginationItem>
                          </React.Fragment>
                        ))}
                      <PaginationItem disabled={page >= totalPages}>
                        <PaginationLink next onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />
                      </PaginationItem>
                    </Pagination>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Card>
            <CardBody>
              <h5 className="card-title mb-2">Send email to non-subscribed users</h5>
              <p className="text-muted small mb-3">
                Send a custom email to users who do not have an active subscription (e.g. reminder to
                resubscribe).
              </p>
              <Button color="primary" onClick={() => openEmailModal('all')}>
                Send email to all non-subscribed users
              </Button>
              <Button color="outline-primary" className="ms-2" onClick={() => openEmailModal('selected')}>
                Select users and send email
              </Button>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={emailModalOpen} toggle={() => setEmailModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setEmailModalOpen(false)}>
          Send email to non-subscribed users
        </ModalHeader>
        <ModalBody>
          <form onSubmit={sendNotSubscribedEmail}>
            <FormGroup>
              <Label>Subject</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm((f) => ({ ...f, subject: e.target.value }))}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Message (plain text)</Label>
              <Input
                type="textarea"
                rows={5}
                value={emailForm.message}
                onChange={(e) => setEmailForm((f) => ({ ...f, message: e.target.value }))}
                required
              />
            </FormGroup>
            {emailTarget === 'all' && (
              <p className="text-muted small">
                {loadingNotSubscribed
                  ? 'Loading list...'
                  : `Email will be sent to ${notSubscribedUsers.length} non-subscribed user(s).`}
              </p>
            )}
            {emailTarget === 'selected' && (
              <>
                <Button
                  type="button"
                  color="outline-secondary"
                  size="sm"
                  onClick={loadNotSubscribedUsers}
                  disabled={loadingNotSubscribed}
                >
                  {loadingNotSubscribed ? 'Loading...' : 'Load non-subscribed users'}
                </Button>
                {notSubscribedUsers.length > 0 && (
                  <div className="mt-2" style={{ maxHeight: 200, overflow: 'auto' }}>
                    {notSubscribedUsers.map((u) => (
                      <label key={u._id} className="d-block mb-1">
                        <Input
                          type="checkbox"
                          checked={selectedUserIds.includes(u._id)}
                          onChange={() => toggleUserSelection(u._id)}
                        />{' '}
                        {u.email} ({u.name})
                      </label>
                    ))}
                  </div>
                )}
                <p className="text-muted small mt-2">
                  Selected: {selectedUserIds.length} user(s)
                </p>
              </>
            )}
            <div className="d-flex gap-2 mt-3">
              <Button color="primary" type="submit" disabled={sendingEmail}>
                {sendingEmail ? 'Sending...' : 'Send email(s)'}
              </Button>
              <Button color="secondary" type="button" onClick={() => setEmailModalOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={!!statusConfirmModal}
        toggle={() => setStatusConfirmModal(null)}
      >
        <ModalHeader toggle={() => setStatusConfirmModal(null)}>
          Confirm status change
        </ModalHeader>
        <ModalBody>
          {statusConfirmModal && (
            <>
              <p className="mb-2">
                Are you sure you want to set this subscription to{' '}
                <strong>{statusConfirmModal.newStatus === 'active' ? 'Active' : 'Cancelled'}</strong>?
              </p>
              <p className="text-muted small mb-0">
                User: {statusConfirmModal.userName || '—'} ({statusConfirmModal.userEmail || '—'})
              </p>
              <div className="d-flex gap-2 mt-3">
                <Button
                  color={statusConfirmModal.newStatus === 'active' ? 'success' : 'danger'}
                  onClick={handleStatusConfirm}
                >
                  Yes, set {statusConfirmModal.newStatus === 'active' ? 'Active' : 'Cancelled'}
                </Button>
                <Button color="secondary" onClick={() => setStatusConfirmModal(null)}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default InvoicesPage;
